/**
 * Subdomain routing utilities.
 * Extract subdomain from current URL for public nonprofit sites vs dashboard.
 * NOS-64: Supports custom domain resolution (e.g. donate.example.org → org by custom_domain).
 */

import {
  ROOT_DOMAIN as NOS_ROOT_DOMAIN,
  isPlatformSubdomain,
} from "../../packages/host-resolution/src/index.ts"

/**
 * Extract subdomain from current URL when on *.getnos.ai.
 * Returns null for dashboard domains (app, localhost, IP), path-based (app.getnos.ai/orgslug),
 * or custom domains (donate.example.org).
 */
export function getSubdomain(): string | null {
  const hostname = window.location.hostname

  // localhost or IP address → dashboard mode
  if (hostname === "localhost" || hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    return null
  }

  // app.getnos.ai or app.* → dashboard mode
  if (hostname === "app.getnos.ai" || hostname.startsWith("app.")) {
    return null
  }

  // Only *.getnos.ai is subdomain-based (e.g. testorg.getnos.ai → "testorg")
  if (hostname.endsWith(`.${NOS_ROOT_DOMAIN}`)) {
    const subdomain = hostname.slice(0, -(NOS_ROOT_DOMAIN.length + 1))
    if (isPlatformSubdomain(subdomain)) {
      return null
    }
    return subdomain
  }

  return null
}

/**
 * NOS-64: Get custom domain from current URL when visiting a verified custom domain.
 * Returns hostname when on a non-NOS domain (e.g. donate.example.org).
 * Returns null for dashboard, localhost, IP, or *.getnos.ai subdomains.
 */
export function getCustomDomain(): string | null {
  const hostname = window.location.hostname

  if (hostname === "localhost" || hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    return null
  }
  if (hostname === "app.getnos.ai" || hostname.startsWith("app.")) {
    return null
  }
  if (hostname.endsWith(`.${NOS_ROOT_DOMAIN}`)) {
    return null
  }
  return hostname
}

/**
 * Check if current URL is the dashboard (no subdomain or custom domain).
 */
export function isDashboard(): boolean {
  return getSubdomain() === null && getCustomDomain() === null
}
